import React, { useEffect, useState } from 'react';
import DateRangePicker, { DateRangePreset } from '@components/common/DateRangePicker';
import Table, { TableHeaderContainerV2 } from '@components/common/Table';
import PaginationComponent from '@components/pagination';
import { userTicketListColumn } from '@data/columns';
import { getUserTicketList } from '@lib/api';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import { asArray } from '@util/array';
import Format from '@util/formatter';
import styled from 'styled-components';

interface TicketTabProps {
  merchantId: number;
  phoneNum: string;
  setIsLoading: (loading: boolean) => void;
}

// '현재 이용중' = 만료되지 않은(현재 유효한) 이용권만 기간 무관 조회 (백엔드 active=1).
// '전체 기간' = 만료된 과거 이력까지 날짜 범위로 조회.
type TicketViewMode = 'active' | 'all';

const TicketTab = ({ merchantId, phoneNum, setIsLoading }: TicketTabProps) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();

  const [viewMode, setViewMode] = useState<TicketViewMode>('active');
  // 전체 기간 모드의 기본 조회 범위는 최근 1년. 더 과거 이력은 시작일을 직접 조정.
  const [startDate, setStartDate] = useState(dayjs().subtract(1, 'year').add(1, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);
  const [totalLength, setTotalLength] = useState(0);
  const [dataList, setDataList] = useState<any[]>([]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const dateRange = Format.formatDateRange(startDate, endDate);
      const { data } = await createRequest(
        getUserTicketList,
        merchantId,
        dateRange.start,
        dateRange.end,
        currentPage,
        postsPerPage,
        phoneNum,
        viewMode === 'active', // active=1: 기간 무시하고 현재 유효한 이용권만
      );
      const ticketRows = asArray<any>(data?.result);
      setDataList(
        ticketRows.map((item) => ({
          ...item,
          T_isUsing:
            item.isUsing === 1 ? (
              <div>사용중</div>
            ) : item.isUsing === 2 ? (
              <div style={{ color: '#F04E36' }}>시간 초과</div>
            ) : item.lastOut ? (
              <div style={{ color: '#53852A' }}>퇴장완료</div>
            ) : (
              <div>사용전</div>
            ),
          type:
            item.type === 1
              ? ' 일회권'
              : item.type === 2
              ? ` 기간권 - ${item.seatType === 0 ? '자유석' : '고정석'}`
              : item.type === 3
              ? ' 시간정액권'
              : item.type === 4
              ? ' 세미나실'
              : item.seatNumber != null
              ? ` 사물함 - ${item.seatNumber}번`
              : ' 사물함',
          start: dayjs(item.startDate).format('YYYY-MM-DD HH:mm:ss'),
          end: dayjs(item.endDate).format('YYYY-MM-DD HH:mm:ss'),
        })),
      );
      setTotalLength(Number(data?.count) || ticketRows.length);
    } catch (error) {
      catchError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (merchantId && phoneNum) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantId, phoneNum, currentPage, startDate, endDate, viewMode]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') setStartDate(value);
    else if (name === 'endDate') setEndDate(value);
    setCurrentPage(1);
  };

  const handleViewModeChange = (mode: TicketViewMode) => {
    if (mode === viewMode) return;
    setViewMode(mode);
    setCurrentPage(1);
  };

  return (
    <>
      <TableHeaderContainerV2>
        <FilterRow>
          <SegmentControl role="tablist">
            <SegmentButton
              type="button"
              role="tab"
              aria-selected={viewMode === 'active'}
              $active={viewMode === 'active'}
              onClick={() => handleViewModeChange('active')}
            >
              현재 이용중
            </SegmentButton>
            <SegmentButton
              type="button"
              role="tab"
              aria-selected={viewMode === 'all'}
              $active={viewMode === 'all'}
              onClick={() => handleViewModeChange('all')}
            >
              전체 기간
            </SegmentButton>
          </SegmentControl>
          {viewMode === 'all' && (
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateChange}
              defaultPreset={DateRangePreset.YEAR}
            />
          )}
        </FilterRow>
      </TableHeaderContainerV2>
      <div className="datatable-container">
        <Table tableHeight={500} data={dataList} columns={userTicketListColumn} striped={true} />
      </div>
      {totalLength > 0 && (
        <PaginationComponent
          setCurrentPage={setCurrentPage}
          totalLength={totalLength}
          postsPerPage={postsPerPage}
          currentPage={currentPage}
        />
      )}
    </>
  );
};

export default TicketTab;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const SegmentControl = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: ${({ theme }) => theme.neutralGray[100]};
  border-radius: 10px;
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.white : theme.neutralGray[500])};
  background: ${({ $active, theme }) => ($active ? theme.primary : 'transparent')};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${({ $active, theme }) => ($active ? theme.white : theme.neutralGray[600])};
  }
`;
